import { sample } from 'effector'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { uploadAllMediaFx } from '@/dialogs/AddMediaDialog/models.ts'
import { $creatorId } from '@/models/creators.ts'
import {
  $canLoadMoreVaultCategoryPages,
  $categoryQueryParam,
  $selectedCategory,
  $vaultCategories,
  getNextVaultCategoriesPageFx,
  getVaultCategoryFx,
  refetchVaultCategories,
  setSelectedCategory,
  updateVaultCategory,
} from '@/models/vault/categories.ts'
import {
  setVaultCategoryCustomOrderFx,
  setVaultCategorySortFx,
} from '@/models/vault/categoryOptions.ts'
import {
  $mediaToCategoryMap,
  $selectedMediaIds,
  $selectedMediaType,
  $vaultMedias,
  addMediaFx,
  deleteMediasFx,
  getNextVaultMediasPageFx,
  mediaMoveInFx,
  mediaMoveOutFx,
  mediaSearchFx,
  refetchVaultMedias,
  setMediaCategoryMap,
  setSelectedMediaIds,
} from '@/models/vault/medias'
import {
  $mediaQueryParam,
  $mediaSort,
  $mediaSortField,
  setMediaQueryParam,
} from '@/models/vault/medias/options.ts'
import { $lastSelectedMediaId } from '@/models/vault/medias/selection.ts'
import { $isVaultOpen } from '@/models/vault/vault.ts'

// refetch vault categories on update
sample({
  clock: [
    setVaultCategorySortFx.doneData,
    setVaultCategoryCustomOrderFx.doneData,
    deleteMediasFx.doneData,
    $categoryQueryParam,
    $creatorId,
  ],
  target: refetchVaultCategories,
})

// trigger categories request
sample({
  clock: $isVaultOpen,
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
    data: $vaultCategories,
  },
  filter: ({ creatorId, isVaultOpen, data }) =>
    // load data only if it was empty
    !!creatorId && isVaultOpen && data === $vaultCategories.defaultState,
  target: getNextVaultCategoriesPageFx,
})

sample({
  clock: refetchVaultCategories,
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
  },
  filter: ({ creatorId, isVaultOpen }) => !!creatorId && isVaultOpen,
  target: getNextVaultCategoriesPageFx,
})

let isInitialCategorySet = false

// check if we are able to get selected category data
sample({
  source: {
    categories: $vaultCategories,
    canLoadMore: $canLoadMoreVaultCategoryPages,
  },
  filter: ({ categories }) => {
    const selectedCatId = +(
      sessionStorage.getItem(LocalStorageKeys.SELECTED_VAULT_CATEGORY) ?? ''
    )

    if (isInitialCategorySet || !categories.length || !selectedCatId) {
      return false
    }

    return categories.some(({ id }) => id === selectedCatId)
  },
  fn: ({ categories }) => {
    const selectedCatId = +(
      sessionStorage.getItem(LocalStorageKeys.SELECTED_VAULT_CATEGORY) ?? ''
    )

    isInitialCategorySet = true

    return categories.find(({ id }) => id === selectedCatId)!
  },
  target: setSelectedCategory,
})

// check should we load more categories to get selected category data
sample({
  source: {
    categories: $vaultCategories,
    canLoadMore: $canLoadMoreVaultCategoryPages,
  },
  filter: ({ categories, canLoadMore }) => {
    const selectedCatId = +(
      sessionStorage.getItem(LocalStorageKeys.SELECTED_VAULT_CATEGORY) ?? ''
    )

    if (
      isInitialCategorySet ||
      !categories.length ||
      !selectedCatId ||
      !canLoadMore
    ) {
      return false
    }

    return !categories.some(({ id }) => id === selectedCatId)
  },
  target: getNextVaultCategoriesPageFx,
})

sample({
  clock: [mediaMoveInFx.doneData, mediaMoveOutFx.doneData],
  target: updateVaultCategory,
})

sample({
  clock: mediaMoveInFx.done,
  source: { mediaIds: $selectedMediaIds, old: $mediaToCategoryMap },
  fn: ({ mediaIds, old }, { params: categoryId }) => {
    return mediaIds.reduce((result, id) => {
      return {
        ...result,
        [id]: result[id] ? [...result[id], categoryId] : [categoryId],
      }
    }, old)
  },
  target: setMediaCategoryMap,
})

sample({
  clock: mediaMoveOutFx.done,
  source: { mediaIds: $selectedMediaIds, old: $mediaToCategoryMap },
  fn: ({ mediaIds, old }, { params: categoryId }) => {
    return mediaIds.reduce((result, id) => {
      return {
        ...result,
        [id]: result[id]?.filter((i) => i !== categoryId) ?? [],
      }
    }, old)
  },
  target: setMediaCategoryMap,
})

// update media -> category map on each update of media items
sample({
  clock: $vaultMedias,
  source: { medias: $vaultMedias, old: $mediaToCategoryMap },
  fn: ({ medias, old }) => {
    return medias.reduce((result, item) => {
      if (!result[item.id]) {
        return {
          ...result,
          [item.id]: item.listStates,
        }
      }

      return result
    }, old)
  },
  target: setMediaCategoryMap,
})

sample({
  clock: deleteMediasFx.doneData,
  fn: () => $selectedMediaIds.defaultState,
  target: setSelectedMediaIds,
})

// trigger medias request
sample({
  clock: $isVaultOpen,
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
    data: $vaultMedias,
    search: $mediaQueryParam,
  },
  filter: ({ creatorId, isVaultOpen, data, search }) => {
    // load data only if it was empty
    return (
      !!creatorId &&
      isVaultOpen &&
      data === $vaultMedias.defaultState &&
      !search
    )
  },
  target: getNextVaultMediasPageFx,
})

sample({
  clock: refetchVaultMedias,
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
    search: $mediaQueryParam,
  },
  filter: ({ creatorId, isVaultOpen, search }) => {
    return !!creatorId && isVaultOpen && !search
  },
  target: getNextVaultMediasPageFx,
})

/**
 * Check do we need to scroll to certain media and is it presented in the current list, if not - load the next media page.
 */
sample({
  clock: getNextVaultMediasPageFx.doneData,
  source: {
    mediaList: $vaultMedias,
    lastSelectedMediaId: $lastSelectedMediaId,
  },
  filter: ({ mediaList, lastSelectedMediaId }, { list, hasMore }) =>
    !!lastSelectedMediaId &&
    !mediaList.some(({ id }) => id === lastSelectedMediaId) &&
    !list.some(({ id }) => id === lastSelectedMediaId) &&
    hasMore,
  target: getNextVaultMediasPageFx,
})

/**
 * Reset media search on a category switch.
 */
sample({ clock: $selectedCategory, fn: () => '', target: setMediaQueryParam })

sample({
  clock: [
    uploadAllMediaFx.done,
    deleteMediasFx.doneData,
    $creatorId,
    $selectedMediaType,
    $selectedCategory,
    $mediaSort,
    $mediaSortField,
  ],
  target: refetchVaultMedias,
})

sample({
  clock: [$isVaultOpen, $creatorId, $mediaQueryParam],
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
    search: $mediaQueryParam,
  },
  filter: ({ creatorId, isVaultOpen, search }) => {
    return !!creatorId && isVaultOpen && !!search
  },
  target: mediaSearchFx,
})

sample({
  clock: refetchVaultMedias,
  source: {
    isVaultOpen: $isVaultOpen,
    creatorId: $creatorId,
    search: $mediaQueryParam,
  },
  filter: ({ creatorId, isVaultOpen, search }) => {
    return !!creatorId && isVaultOpen && !!search
  },
  target: mediaSearchFx,
})

/**
 * Update media category when we upload new media.
 * If no categoryId, that means it is `all`, and we will update it in another sample.
 */
sample({
  clock: addMediaFx.done,
  filter: ({ params: { selectedCategoryId } }) => !!selectedCategoryId,
  fn: ({ params: { selectedCategoryId } }) => selectedCategoryId!,
  target: getVaultCategoryFx,
})

/**
 * After media upload always need to update `all` category.
 */
sample({
  clock: addMediaFx.done,
  fn: () => 0,
  target: getVaultCategoryFx,
})
